import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { MapPin, Phone, Clock, Navigation, Anchor } from "lucide-react";



interface UnitData {
    name: string;
    state: string;
    address: string;
    phone: string;
    whatsapp: string;
    hours: string;
    services: string[];
    mapEmbedUrl: string;
    highlight?: string;
}

const units: UnitData[] = [
    {
        name: "Balneário Camboriú",
        state: "Santa Catarina",
        address: "R. Dom Henrique, 1200 — Jardim Iate Clube, Balneário Camboriú/SC",
        phone: "(47) 99146-8851",
        whatsapp: `https://wa.me/5547991468851?text=${encodeURIComponent("Olá! Gostaria de informações sobre a unidade de Balneário Camboriú.")}`,
        hours: "Seg a Sex: 08h–18h | Sáb: 09h–13h",
        services: ["Escola Náutica", "Despachante Náutico", "Consultoria"],
        mapEmbedUrl:
            "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3554.4702055711!2d-48.6322604!3d-27.015304099999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94d8b71d5bf80a2b%3A0x3c520e49d7133a!2sJL%20Despachante%20e%20Escola%20N%C3%A1utica!5e0!3m2!1spt-BR!2sbr!4v1771013852042!5m2!1spt-BR!2sbr",
        highlight: "Sede",
    },
    {
        name: "São Francisco do Sul",
        state: "Santa Catarina",
        address: "Lgo. Almirante Moraes Rego, 156 — Centro, São Francisco do Sul/SC",
        phone: "(47) 99206-9885",
        whatsapp: `https://wa.me/5547992069885?text=${encodeURIComponent("Olá! Gostaria de informações sobre a unidade de São Francisco do Sul.")}`,
        hours: "Seg a Sex: 08h–18h | Sáb: 09h–13h",
        services: ["Escola Náutica", "Despachante Náutico", "Consultoria"],
        mapEmbedUrl:
            "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3578.498978900763!2d-48.6399038!3d-26.245464300000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94d94f32b9b822f9%3A0xf9d3ce532e7d2b85!2sJL%20Despachante%20e%20Escola%20N%C3%A1utica!5e0!3m2!1spt-BR!2sbr!4v1771014809574!5m2!1spt-BR!2sbr",
    },
    {
        name: "Barra do Sul",
        state: "Santa Catarina",
        address: "Av. Amândio Cabral, 491 — Centro, Balneário Barra do Sul/SC",
        phone: "(47) 99206-9885",
        whatsapp: `https://wa.me/5547992069885?text=${encodeURIComponent("Olá! Gostaria de informações sobre a unidade de Barra do Sul.")}`,
        hours: "Seg a Sex: 08h–18h",
        services: ["Escola Náutica", "Despachante Náutico", "Consultoria"],
        mapEmbedUrl:
            "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3571.8813248208917!2d-48.609568723824985!3d-26.459554074423874!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94d9311acd439c09%3A0xc21c3b139cebc318!2sAv.%20Am%C3%A2ndio%20Cabral%2C%20491%2C%20Balne%C3%A1rio%20Barra%20do%20Sul%20-%20SC%2C%2089247-000!5e0!3m2!1spt-BR!2sbr!4v1772135307517!5m2!1spt-BR!2sbr",
    },
    {
        name: "Canoinhas",
        state: "Santa Catarina",
        address: "Rua Irmã Maria Felicitas, 631 — Centro, Canoinhas/SC",
        phone: "(47) 99146-8851",
        whatsapp: `https://wa.me/5547991468851?text=${encodeURIComponent("Olá! Gostaria de informações sobre a unidade de Canoinhas.")}`,
        hours: "Seg a Sex: 08h–18h",
        services: ["Escola Náutica", "Despachante Náutico", "Consultoria"],
        mapEmbedUrl:
            "https://www.google.com/maps?q=Rua+Irm%C3%A3+Maria+Felicitas,+631,+Centro,+Canoinhas+-+SC&output=embed",
    },
];

const UnitCard = ({
    unit,
    index,
}: {
    unit: UnitData;
    index: number;
}) => {
    const slug = unit.name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "-");

    return (
        <motion.div
            id={slug}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:shadow-primary/5 transition-all duration-500"
        >
            {/* Map */}
            <div className="relative h-48 w-full bg-slate-100">
                <iframe
                    src={unit.mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`Mapa ${unit.name}`}
                    className="grayscale group-hover:grayscale-0 transition-all duration-700"
                />
                {unit.highlight && (
                    <div className="absolute top-3 right-3">
                        <span className="bg-gold text-primary text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-md">
                            {unit.highlight}
                        </span>
                    </div>
                )}
            </div>

            {/* Info */}
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="font-heading text-xl font-semibold text-primary">
                            {unit.name}
                        </h3>
                        <span className="text-xs text-muted-foreground uppercase tracking-wider">
                            {unit.state}
                        </span>
                    </div>
                    <Anchor className="w-5 h-5 text-gold/50" />
                </div>

                <div className="space-y-3 mb-5">
                    <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                        <span className="text-sm text-muted-foreground leading-relaxed">
                            {unit.address}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gold shrink-0" />
                        <span className="text-sm text-muted-foreground">{unit.phone}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-gold shrink-0" />
                        <span className="text-sm text-muted-foreground">{unit.hours}</span>
                    </div>
                </div>

                {/* Services Tags */}
                <div className="flex flex-wrap gap-1.5 mb-5">
                    {unit.services.map((s) => (
                        <span
                            key={s}
                            className="text-[10px] font-medium text-primary/70 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10"
                        >
                            {s}
                        </span>
                    ))}
                </div>

                {/* CTA */}
                <div className="flex gap-3">
                    <a
                        href={unit.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                        >
                            <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                        </svg>
                        WhatsApp
                    </a>
                    <a
                        href={`https://www.google.com/maps/search/${encodeURIComponent(unit.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-muted-foreground hover:border-gold/30 hover:text-gold transition-all"
                    >
                        <Navigation className="w-3.5 h-3.5" />
                        Rota
                    </a>
                </div>
            </div>
        </motion.div>
    );
};

const UnidadesPage = () => {
    return (
        <div className="relative bg-background min-h-screen">
            <Header />

            {/* Hero */}
            <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 bg-primary overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gold/5 via-transparent to-transparent" />
                <div className="container mx-auto px-6 lg:px-12 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-xs uppercase tracking-[0.3em] text-gold mb-4 block">
                            Nossas Bases
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-medium text-primary-foreground mb-4">
                            Onde Estamos.
                        </h1>
                        <p className="text-lg text-primary-foreground/60 max-w-2xl mx-auto">
                            Do litoral ao planalto de Santa Catarina. Encontre a unidade JL
                            mais próxima de você.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Units Grid */}
            <section className="py-16 sm:py-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
                        {units.map((unit, i) => (
                            <UnitCard key={unit.name} unit={unit} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Bottom */}
            <section className="py-16 bg-primary">
                <div className="container mx-auto px-6 lg:px-12 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="font-heading text-2xl sm:text-3xl font-medium text-primary-foreground mb-3">
                            Não encontrou sua região?
                        </h2>
                        <p className="text-primary-foreground/60 mb-6">
                            Atendemos em todo o território nacional. Entre em contato e
                            resolveremos de qualquer lugar.
                        </p>
                        <a
                            href={`https://wa.me/5547992069885?text=${encodeURIComponent("Olá! Gostaria de saber se vocês atendem na minha região.")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gold text-primary font-medium text-sm hover:bg-gold/90 transition-colors shadow-lg"
                        >
                            Falar com a JL
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                                />
                            </svg>
                        </a>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default UnidadesPage;
